/**
 * M15: Dashboard list of campaigns with progress, grouped by status.
 */
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { PermissionGate } from "@/components/PermissionGate"
import { Navigate } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useOrgStore } from "@/stores/orgStore"
import { useCampaigns } from "@/hooks/useCampaigns"
import { fetchCampaignProgress, type CampaignProgress } from "@/hooks/useCampaignDonations"
import { CampaignProgressBar } from "@/components/campaigns/CampaignProgressBar"
import type { Tables } from "@/types/database"

type CampaignRow = Tables<"campaigns">

function campaignBucket(c: Pick<CampaignRow, "start_date" | "end_date">): "active" | "upcoming" | "past" {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const end = c.end_date ? new Date(c.end_date + "T12:00:00") : null
  const start = c.start_date ? new Date(c.start_date + "T12:00:00") : null
  if (end) {
    end.setHours(0, 0, 0, 0)
    if (end < today) return "past"
  }
  if (start) {
    start.setHours(0, 0, 0, 0)
    if (start > today) return "upcoming"
  }
  return "active"
}

function daysRemaining(endDate: string | null): number | null {
  if (!endDate) return null
  const end = new Date(endDate + "T12:00:00")
  end.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.ceil((end.getTime() - today.getTime()) / 86400000)
}

export function CampaignsPage() {
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null
  const { campaigns, loading } = useCampaigns(orgId)
  const [progressById, setProgressById] = useState<Record<string, CampaignProgress>>({})

  const idsKey = useMemo(() => campaigns.map((c) => c.id).sort().join(","), [campaigns])

  useEffect(() => {
    if (!orgId || campaigns.length === 0) {
      setProgressById({})
      return
    }
    let cancelled = false
    ;(async () => {
      const entries = await Promise.all(
        campaigns.map(async (c) => {
          try {
            const p = await fetchCampaignProgress(c.id)
            return [c.id, p] as const
          } catch {
            return [
              c.id,
              { raised_cents: 0, donor_count: 0, fundraiser_count: 0 },
            ] as const
          }
        })
      )
      if (!cancelled) setProgressById(Object.fromEntries(entries))
    })()
    return () => {
      cancelled = true
    }
  }, [orgId, idsKey, campaigns])

  const grouped = useMemo(() => {
    const buckets: Record<"active" | "upcoming" | "past", CampaignRow[]> = {
      active: [],
      upcoming: [],
      past: [],
    }
    for (const c of campaigns) {
      buckets[campaignBucket(c)].push(c)
    }
    return buckets
  }, [campaigns])

  return (
    <PermissionGate permission="can_manage_content" fallback={<Navigate to="/dashboard" replace />}>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold">Campaigns</h1>
          <p className="text-muted-foreground text-sm">
            Track progress and peer-to-peer fundraisers for each campaign.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : campaigns.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No campaigns yet.{" "}
              <Link to="/dashboard/builder" className="underline hover:text-foreground">
                Create one in the website builder
              </Link>
              {" "}under Content → Campaigns.
            </CardContent>
          </Card>
        ) : (
          (["active", "upcoming", "past"] as const).map((bucket) => {
            const list = grouped[bucket]
            if (list.length === 0) return null
            const title =
              bucket === "active" ? "Active" : bucket === "upcoming" ? "Upcoming" : "Past"
            return (
              <section key={bucket} data-testid={`campaigns-group-${bucket}`}>
                <h2 className="mb-3 text-lg font-semibold">{title}</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {list.map((c) => {
                    const p = progressById[c.id] ?? {
                      raised_cents: 0,
                      donor_count: 0,
                      fundraiser_count: 0,
                    }
                    const ended = campaignBucket(c) === "past"
                    const days = daysRemaining(c.end_date)
                    return (
                      <Card key={c.id}>
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between gap-2">
                            <CardTitle className="text-base">{c.title}</CardTitle>
                            {c.is_published ? (
                              <Badge variant="default">Published</Badge>
                            ) : (
                              <Badge variant="secondary">Draft</Badge>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <CampaignProgressBar
                            raisedCents={p.raised_cents}
                            goalCents={c.goal_cents}
                            ended={ended}
                          />
                          <div className="text-xs text-muted-foreground">
                            {ended ? (
                              <span>Campaign ended</span>
                            ) : days != null && days >= 0 ? (
                              <span>{days} days remaining</span>
                            ) : (
                              <span>No end date</span>
                            )}
                            {" · "}
                            {p.donor_count} donors · {p.fundraiser_count} fundraisers
                          </div>
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/dashboard/campaigns/${c.id}`}>View details</Link>
                          </Button>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              </section>
            )
          })
        )}
      </div>
    </PermissionGate>
  )
}
